import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import validator from "validator";
import LogoComponent from "../../components//Logos/LogoComponent";
import SubmitButton from "../../components/buttons/SubmitButton";
import ActionButton from "../../components/buttons/ActionButton";
import injectScriptToDOM from "../../scripts/injectScriptToDOM";
import { useRateLimiter } from "hooks/useRateLimiter";
import { CurrentUserContext } from "../../App";
import { Capacitor } from "@capacitor/core";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";
const isNative = Capacitor.isNativePlatform();

export let loginController;

export default function LoginPage() {
  const rateLimitEvent = new Event("checkLimit");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { currentUser, setCurrentUser } = React.useContext(CurrentUserContext);

  useRateLimiter();

  // Clear stale rate limiter data from old version
  useEffect(() => {
    localStorage.removeItem("recentAttemptTimestamps");
    localStorage.removeItem("recentAttemptsCount");
  }, []);

  // Load Google Identity Services script (web only)
  useEffect(() => {
    if (isNative) return; // Skip on native — we use the Capacitor plugin instead
    injectScriptToDOM("https://accounts.google.com/gsi/client", true);
    return () => {
      document.getElementById("google-oauth2")?.remove();
    };
  }, [currentUser]);

  const onFormSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEmailError("");
    setPasswordError("");

    let formIsValid = true;

    if (!validator.isEmail(email)) {
      formIsValid = false;
      setEmailError("Please enter a valid email");
    }

    if (password.length < 8) {
      formIsValid = false;
      setPasswordError("The password must be 8 characters or longer");
    }

    if (formIsValid) {
      setIsLoading(true);
      loginController = new AbortController();
      window.dispatchEvent(rateLimitEvent);
      checkAccountExists((accountExists) => {
        if (accountExists) {
          auth();
        } else if (
          window.confirm(
            "An account does not exist for " +
              email +
              ". Do you want to create a new account?"
          )
        ) {
          setIsLoading(false);
          navigate("/login/signup", {
            state: { email, password },
          });
        } else {
          setIsLoading(false);
        }
      });
    }
  };

  const checkAccountExists = (callback) => {
    fetch(`${API_URL}/check-account`, {
      signal: loginController.signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    })
      .then((r) => r.json())
      .then((r) => callback(r?.userExists))
      .catch(() => {
        setIsLoading(false);
        alert("Too many login attempts. Please try again later.");
      });
  };

  const auth = () => {
    fetch(`${API_URL}/login`, {
      signal: loginController.signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    })
      .then((r) => r.json())
      .then((r) => {
        setIsLoading(false);
        if ("success" === r.message) {
          setCurrentUser(r.user);
          localStorage.setItem("currentUser", JSON.stringify(r.user));
          navigate("/memories");
        } else if ("Wrong" === r.message.slice(0, 5)) {
          setPasswordError("Wrong Password");
        }
      })
      .catch(() => {
        setIsLoading(false);
        alert("Too many login attempts. Please try again later.");
      });
  };

  // Send Google credential/token to backend
  const sendGoogleCredentialToBackend = (credentials) => {
    loginController = new AbortController();
    window.dispatchEvent(rateLimitEvent);
    fetch(`${API_URL}/g-signin`, {
      signal: loginController.signal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credentials),
    })
      .then((r) => r.json())
      .then((r) => {
        setGoogleLoading(false);
        if ("success" === r.message) {
          setCurrentUser(r.user);
          localStorage.setItem("currentUser", JSON.stringify(r.user));
          if ("login" === r.mode) {
            navigate("/memories");
          } else if ("signup" === r.mode) {
            navigate("/login/emailVerification");
          }
        } else {
          alert(r.message);
        }
      })
      .catch(() => {
        setGoogleLoading(false);
        alert("Too many login attempts. Please try again later.");
      });
  };

  // Web: Google Identity Services callback
  window.handleCredentialResponse = (credentials) => {
    sendGoogleCredentialToBackend(credentials);
  };

  // Native: Capacitor Google Auth
  const handleNativeGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      const { GoogleAuth } = await import("@codetrix-studio/capacitor-google-auth");
      await GoogleAuth.initialize({
        clientId: "86117139794-meq950crbsqsr5a6o3iiuptqisqdurv4.apps.googleusercontent.com",
        scopes: ["profile", "email"],
        grantOfflineAccess: true,
      });
      const user = await GoogleAuth.signIn();
      // The plugin returns an idToken in authentication.idToken
      const idToken = user.authentication?.idToken;
      if (idToken) {
        sendGoogleCredentialToBackend({ credential: idToken });
      } else {
        setGoogleLoading(false);
        alert("Google sign-in failed: no token received.");
      }
    } catch (err) {
      setGoogleLoading(false);
      console.error("Native Google Sign-In error:", err);
      if (err.message !== "The user canceled the sign-in flow.") {
        alert("Google sign-in failed. Please try again.");
      }
    }
  };

  const logOut = () => {
    localStorage.removeItem("currentUser");
    setCurrentUser(null);
  };

  return (
    <div className="d-flex min-vh-100 justify-content-center align-items-center"
      style={{ padding: "env(safe-area-inset-top) 0 env(safe-area-inset-bottom)" }}
    >
      <div
        style={{ maxWidth: "26rem", width: "100%", padding: "0 1.5rem" }}
        className="d-flex flex-column"
      >
        <div className="mb-4">
          <LogoComponent />
        </div>
        <div className="d-flex flex-column justify-content-center align-items-center gap-2">
          <span
            className="heading-secondary mb-3"
            style={{ fontSize: "2.4rem" }}
          >
            Greetings!
          </span>

          {currentUser?.email ? (
            <div className="">
              <div className="mb-2">Logged in as {currentUser.email}</div>
              <ActionButton
                buttonText={"Log Out"}
                buttonValue={"logout"}
                buttonAction={logOut}
              />
            </div>
          ) : (
            <div className="w-100">
              {/* Login Form */}
              <form onSubmit={onFormSubmit} noValidate className="mb-4 w-100">
                <div className="mb-2">
                  <input
                    className="form-control"
                    id="inputEmail"
                    aria-describedby="emailHelp"
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{ fontSize: "1.6rem" }}
                  />
                  <label className="errorLabel mt-2">{emailError}</label>
                </div>
                <div className="mb-2">
                  <input
                    className="form-control w-100"
                    id="inputPassword"
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ fontSize: "1.6rem" }}
                  />
                  <label className="errorLabel mt-2">{passwordError}</label>
                </div>
                <SubmitButton buttonText={"Log In"} buttonValue={"login"} loading={isLoading} />
              </form>

              {/* FORGOT PASSWORD */}
              <div className="d-flex flex-column gap-2 text-center">
                <Link
                  to="forgotpassword"
                  className="mb-4 link-light link-underline-opacity-0"
                  style={{ fontSize: "1.2rem" }}
                >
                  Forgot Password?
                </Link>
                <div className="d-flex align-items-center gap-3 mb-4">
                  <hr className="flex-grow-1 m-0" style={{ borderColor: "white" }} />
                  <span style={{ fontSize: "1.2rem" }}>OR</span>
                  <hr className="flex-grow-1 m-0" style={{ borderColor: "white" }} />
                </div>
              </div>

              {/* GOOGLE SIGN-IN */}
              <div className="d-flex flex-column align-items-center mb-4">
                {isNative ? (
                  /* Native app: custom Google button using Capacitor plugin */
                  <button
                    onClick={handleNativeGoogleSignIn}
                    disabled={googleLoading}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      width: "100%",
                      maxWidth: "300px",
                      padding: "12px 20px",
                      backgroundColor: "#131314",
                      border: "1px solid #8e918f",
                      borderRadius: "20px",
                      cursor: googleLoading ? "not-allowed" : "pointer",
                      opacity: googleLoading ? 0.6 : 1,
                    }}
                  >
                    {/* Google "G" logo */}
                    <svg width="18" height="18" viewBox="0 0 48 48">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    </svg>
                    <span style={{ color: "#e3e3e3", fontSize: "14px", fontWeight: 500 }}>
                      {googleLoading ? "Signing in..." : "Sign in with Google"}
                    </span>
                  </button>
                ) : (
                  /* Web: Google Identity Services rendered button */
                  <>
                    <div
                      id="g_id_onload"
                      data-client_id="86117139794-meq950crbsqsr5a6o3iiuptqisqdurv4.apps.googleusercontent.com"
                      data-context="signin"
                      data-ux_mode="popup"
                      data-callback="handleCredentialResponse"
                      data-itp_support="true"
                      data-use_fedcm_for_prompt="true"
                    ></div>
                    <div
                      className="g_id_signin"
                      data-type="standard"
                      data-shape="rectangular"
                      data-theme="filled_black"
                      data-text="signin_with"
                      data-size="large"
                      data-logo_alignment="left"
                    ></div>
                  </>
                )}
              </div>

              {/* SIGN UP LINK */}
              <div className="text-center">
                <span style={{ fontSize: "1.4rem" }}>
                  Need an account?{" "}
                  <Link
                    to={{ pathname: "signup", state: { email, password } }}
                    className="link-light link-offset-1"
                  >
                    SIGN UP
                  </Link>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
